import { useReducer } from 'react'
import { getAuthState, __isDebugOn } from '@sparky/framework'

import type { Reducer, RefObject } from 'react'
import type {
  Tsg,
  ReadonlyTsgTree,
  ReadonlyTsgInstances,
  AppManifests,
  AuthState,
  Instance,
} from '@sparky/framework'
import type { Action } from './actions'

export type TsgAppInstances = Instance[] & { tsg: Tsg }

export const authStateRef = Object.freeze(Object.defineProperties({}, {
  current: { get: getAuthState }
})) as RefObject<AuthState>

export type SwitcherStoreState = {
  disabled: boolean
  isOpen: boolean
  isHub: boolean
  isSingleTenantOnly: boolean
  hasAscPortal: boolean
  hasAscRole?: boolean
  currentPortalDisplayAppId?: string
  contextTsg?: Tsg
  tsg?: Tsg
  tsgs: ReadonlyTsgTree
  availableApps?: AppManifests
  instances?: ReadonlyTsgInstances
  globalInstances?: ReadonlyTsgInstances
  tsgAppInstances?: TsgAppInstances
  apps?: AppManifests
}

export type Dispatch = (action: Action) => void
export type StateRef = {
  current: SwitcherStoreState
  dispatch: Dispatch
}
export type Store = {
  dispatch: Dispatch
  getState: () => SwitcherStoreState
  stateRef: StateRef
}

export type OpenTenantSwitcherEvent = {
  open?: boolean
  tsg_id?: string
}

export const stateRef: StateRef = Object.seal({ // same as createRef
  current: {} as SwitcherStoreState, // ensure not null
  dispatch: (() => {
    throw new Error('state store is not initialized yet')
  }) as Dispatch
})

export type ErrorObject = {
  msg?: string
  message?: string
  error?: string
  statusCode?: number
}

export const getState = (): SwitcherStoreState => stateRef.current

export const dispatch: Dispatch = (action) => stateRef.dispatch(action)

export const store = {
  dispatch,
  getState,
  stateRef,
} as Store

export const useStoreReducer = (reducer: Reducer<SwitcherStoreState, Action>, initState: {}, initializer: (initState: {}) => SwitcherStoreState): SwitcherStoreState => {
  const [state, dispatchFn] = useReducer(
    (state: SwitcherStoreState, action: Action) => {
      const newState = reducer(state, action)
      stateRef.current = newState
      return newState
    },
    initState,
    initializer,
  )
  if (stateRef.dispatch !== dispatchFn) {
    stateRef.dispatch = dispatchFn
  }
  return state
}

if (__isDebugOn()) {
  let commonModule = window.__SPARKY_LIBS__.commonModule
  if (!commonModule) {
    commonModule = {}
    Object.defineProperties(window.__SPARKY_LIBS__.__proto__, {
      commonModule: {
        value: commonModule
      }
    })
  }
  commonModule.switcherStore = store
  commonModule.switcherStateRef = stateRef
}

export default store
